/// Per-session thunderstorm spawner + updater.
///
/// Owns the live `thunderstorms[]` array. Three responsibilities:
///   1. Schedule + roll new storms on a 180–300 s sim-second cadence
///      (gated by the active difficulty's `thunderstormChance`).
///   2. Each tick, advance every storm: phase transition, size/speed
///      lerp, course-jitter, position drift. Remove on lifespan
///      expiry or world-boundary exit.
///   3. Surface a one-time "warning" event 30–60 s before each spawn
///      so the chrome can post a readback toast.
///
/// Time-keeping is sim-seconds only. The engine never reads
/// `Date.now()`; the host passes scaled dt and accumulates internal
/// timers. That guarantees flow scales with the time multiplier and
/// makes everything deterministic under a seeded RNG.

import type { RandomSource } from './rng'
import {
  deriveStormState,
  lifespanMultiplierFor,
  speedRangeForSize,
  type Thunderstorm,
} from './Thunderstorm'

/// Minimum and maximum sim-seconds between spawn rolls. iOS uses
/// 180–300 s (3–5 min). The roll itself is gated by the difficulty's
/// thunderstormChance, so a level with chance=0 never spawns.
const SPAWN_INTERVAL_MIN_SEC = 180
const SPAWN_INTERVAL_MAX_SEC = 300

/// Lead time between the warning event and the actual storm
/// appearing on the radar. iOS uses 30–60 s; preserved for parity.
const WARNING_LEAD_MIN_SEC = 30
const WARNING_LEAD_MAX_SEC = 60

/// Hard cap on simultaneously active storms. iOS uses 3.
const MAX_ACTIVE = 3

/// Course jitter cadence — after a storm spawns, every 30–60 sim
/// seconds nudge its direction by ±5–10°.
const COURSE_CHANGE_MIN_SEC = 30
const COURSE_CHANGE_MAX_SEC = 60
const COURSE_VARIATION_MIN_DEG = 5
const COURSE_VARIATION_MAX_DEG = 10

/// Spawns originate within this many NM of the radar centre, on one
/// of the four cardinal sides. Direction is roughly toward centre
/// with ±30° variation, so storms tend to drift across the radar
/// rather than off the edges.
const SPAWN_RADIUS_NM = 20
const SPAWN_DIRECTION_VARIATION_DEG = 30

/// Storm radius bounds in NM. Smaller storms move faster (see
/// `speedRangeForSize`).
const SIZE_MIN_NM = 3
const SIZE_MAX_NM = 8

/// Base lifespan multiplied by the difficulty multiplier. iOS:
/// Novice 5 min → Master 10 min.
const LIFESPAN_BASE_SEC = 300

/// Storms drift this far past the world half-extent before being
/// culled.
const REMOVAL_MARGIN_RATIO = 0.2

export interface WeatherEngineOptions {
  /// Per-NM-to-pixel ratio (the same value `GameSession.pxPerNM`
  /// holds). Storm position is in pixel space.
  pxPerNM: number
  /// World half-extent in pixels — defines spawn box and removal
  /// boundary (full world = `worldPx`, so half = `worldPx/2`).
  worldPx: number
  /// Per-difficulty roll for whether a tick spawns a storm at all.
  /// 0 disables weather entirely (timer never fires the spawn path).
  thunderstormChance: number
  /// Determines lifespan multiplier (Novice → Master = 1.0× → 2.0×).
  difficultyName: string
  rng: RandomSource
}

/// Per-pending-spawn record. The warning fires immediately when the
/// timer rolls; the storm itself is created `leadSec` later. We
/// stash everything needed for spawn-time so the engine doesn't
/// re-roll RNG between warning and spawn (which would silently
/// desync if the spawn interval changes mid-flight).
interface PendingSpawn {
  remainingLeadSec: number
}

export interface WeatherEvent {
  kind: 'warning' | 'spawned' | 'phaseChanged' | 'removed'
  storm: Thunderstorm
  /// For `phaseChanged`: the previous phase. Otherwise undefined.
  previousPhase?: Thunderstorm['phase']
}

export class WeatherEngine {
  readonly storms: Thunderstorm[] = []
  private readonly opts: WeatherEngineOptions

  /// Sim-seconds until the next spawn-roll. Re-rolled after every
  /// roll, regardless of whether the chance gate succeeded.
  private secondsUntilNextRoll: number
  /// Pending spawns whose warning fired but whose storm hasn't yet
  /// appeared. Drained when their lead time expires.
  private pendingSpawns: PendingSpawn[] = []
  private idCounter = 0

  constructor(opts: WeatherEngineOptions) {
    this.opts = opts
    // Stagger first roll so a fresh session doesn't immediately
    // produce a warning toast on tick 0.
    this.secondsUntilNextRoll = randInRange(
      opts.rng,
      SPAWN_INTERVAL_MIN_SEC,
      SPAWN_INTERVAL_MAX_SEC,
    )
  }

  /// Convenience for tests that want a snapshot of internal state.
  get pendingSpawnCount(): number {
    return this.pendingSpawns.length
  }

  /// Advance everything by `dtSec` (already time-scaled). Returns
  /// any events the host should surface (warning toasts, phase
  /// transition logging hooks, etc.).
  tick(dtSec: number): WeatherEvent[] {
    const events: WeatherEvent[] = []

    // 1. Roll-the-dice for new spawns.
    this.secondsUntilNextRoll -= dtSec
    if (this.secondsUntilNextRoll <= 0) {
      this.secondsUntilNextRoll = randInRange(
        this.opts.rng,
        SPAWN_INTERVAL_MIN_SEC,
        SPAWN_INTERVAL_MAX_SEC,
      )
      if (this.shouldSpawn()) {
        const leadSec = randInRange(
          this.opts.rng,
          WARNING_LEAD_MIN_SEC,
          WARNING_LEAD_MAX_SEC,
        )
        this.pendingSpawns.push({ remainingLeadSec: leadSec })
        // The "warning" event fires now; we synthesise a placeholder
        // storm record so the host can render a generic toast — no
        // position info is included since the storm hasn't been
        // placed yet.
        events.push({ kind: 'warning', storm: this.placeholderForWarning() })
      }
    }

    // 2. Drain any pending spawns whose lead has elapsed.
    for (let i = this.pendingSpawns.length - 1; i >= 0; i--) {
      const p = this.pendingSpawns[i]!
      p.remainingLeadSec -= dtSec
      if (p.remainingLeadSec <= 0) {
        this.pendingSpawns.splice(i, 1)
        const storm = this.spawnNow()
        if (storm) {
          this.storms.push(storm)
          events.push({ kind: 'spawned', storm })
        }
      }
    }

    // 3. Advance every active storm.
    const halfWorld = this.opts.worldPx / 2
    const removalBound = halfWorld + halfWorld * 2 * REMOVAL_MARGIN_RATIO
    for (let i = this.storms.length - 1; i >= 0; i--) {
      const storm = this.storms[i]!
      storm.ageSec += dtSec

      if (storm.ageSec >= storm.maxLifespanSec) {
        this.storms.splice(i, 1)
        events.push({ kind: 'removed', storm })
        continue
      }

      const previousPhase = storm.phase
      const next = deriveStormState(
        storm.baseSizeNM,
        storm.baseSpeedKts,
        storm.ageSec,
        storm.maxLifespanSec,
      )
      storm.phase = next.phase
      storm.sizeNM = next.sizeNM
      storm.speedKts = next.speedKts
      if (previousPhase !== storm.phase) {
        events.push({ kind: 'phaseChanged', storm, previousPhase })
      }

      // Course jitter — fires when the scheduled time arrives.
      // We schedule from age (sim-seconds since spawn) so the timer
      // automatically scales with the time multiplier.
      if (storm.ageSec >= storm.nextDirectionChangeAtSec) {
        const variation =
          (this.opts.rng() < 0.5 ? -1 : 1) *
          randInRange(this.opts.rng, COURSE_VARIATION_MIN_DEG, COURSE_VARIATION_MAX_DEG)
        storm.directionDeg = wrap360(storm.directionDeg + variation)
        storm.nextDirectionChangeAtSec =
          storm.ageSec +
          randInRange(this.opts.rng, COURSE_CHANGE_MIN_SEC, COURSE_CHANGE_MAX_SEC)
      }

      // Position update. directionDeg is compass-degree;
      // convert to math-radians (0=East, CCW): θ = (90 - heading) × π/180.
      const speedPxPerSec = (storm.speedKts * this.opts.pxPerNM) / 3600
      const distancePx = speedPxPerSec * dtSec
      const headingRad = ((90 - storm.directionDeg) * Math.PI) / 180
      storm.x += Math.cos(headingRad) * distancePx
      storm.y += Math.sin(headingRad) * distancePx

      if (
        Math.abs(storm.x) > removalBound ||
        Math.abs(storm.y) > removalBound
      ) {
        this.storms.splice(i, 1)
        events.push({ kind: 'removed', storm })
      }
    }

    return events
  }

  /// Returns true if a new spawn should be scheduled this roll. Cap
  /// counts both currently-active storms and pending-warning ones so
  /// rapid rolls during a high-chance level don't pile up beyond the
  /// MAX_ACTIVE budget.
  private shouldSpawn(): boolean {
    if (this.opts.thunderstormChance <= 0) return false
    if (this.storms.length + this.pendingSpawns.length >= MAX_ACTIVE) return false
    return this.opts.rng() < this.opts.thunderstormChance
  }

  /// Build a fully-initialised storm at a random edge, placed within
  /// SPAWN_RADIUS_NM of the centre with a roughly-toward-centre
  /// drift direction.
  private spawnNow(): Thunderstorm | null {
    const { rng, pxPerNM, difficultyName } = this.opts
    const spawnRadiusPx = SPAWN_RADIUS_NM * pxPerNM
    const distance = rng() * spawnRadiusPx
    const edge = Math.floor(rng() * 4)
    let x: number
    let y: number
    switch (edge) {
      case 0: // North
        x = (rng() * 2 - 1) * spawnRadiusPx
        y = distance
        break
      case 1: // East
        x = distance
        y = (rng() * 2 - 1) * spawnRadiusPx
        break
      case 2: // South
        x = (rng() * 2 - 1) * spawnRadiusPx
        y = -distance
        break
      default: // West
        x = -distance
        y = (rng() * 2 - 1) * spawnRadiusPx
        break
    }

    // Direction roughly toward (0,0). atan2 returns math-radian angle;
    // convert to compass-degree via 90 - degrees(atan2). Then jitter
    // ±SPAWN_DIRECTION_VARIATION_DEG.
    const dxToCentre = -x
    const dyToCentre = -y
    const towardRad = Math.atan2(dyToCentre, dxToCentre)
    let directionDeg = 90 - (towardRad * 180) / Math.PI
    directionDeg += (rng() * 2 - 1) * SPAWN_DIRECTION_VARIATION_DEG
    directionDeg = wrap360(directionDeg)

    const baseSizeNM = SIZE_MIN_NM + rng() * (SIZE_MAX_NM - SIZE_MIN_NM)
    const range = speedRangeForSize(baseSizeNM)
    const baseSpeedKts = range.min + rng() * (range.max - range.min)
    const maxLifespanSec = LIFESPAN_BASE_SEC * lifespanMultiplierFor(difficultyName)

    // Initial values match the forming-phase head: 50% size, 70% speed.
    const initialSize = baseSizeNM * 0.5
    const initialSpeed = baseSpeedKts * 0.7
    const nextChangeIn = randInRange(rng, COURSE_CHANGE_MIN_SEC, COURSE_CHANGE_MAX_SEC)

    this.idCounter += 1
    return {
      id: `storm-${this.idCounter}`,
      x,
      y,
      directionDeg,
      sizeNM: initialSize,
      baseSizeNM,
      speedKts: initialSpeed,
      baseSpeedKts,
      ageSec: 0,
      nextDirectionChangeAtSec: nextChangeIn,
      phase: 'forming',
      maxLifespanSec,
    }
  }

  /// A throw-away record used purely so the warning event has
  /// non-null `storm`. The host doesn't render the placeholder —
  /// the warning toast is generic ("⚠️ Thunderstorm forming").
  private placeholderForWarning(): Thunderstorm {
    return {
      id: 'pending',
      x: 0,
      y: 0,
      directionDeg: 0,
      sizeNM: 0,
      baseSizeNM: 0,
      speedKts: 0,
      baseSpeedKts: 0,
      ageSec: 0,
      nextDirectionChangeAtSec: 0,
      phase: 'forming',
      maxLifespanSec: 0,
    }
  }
}

function randInRange(rng: RandomSource, min: number, max: number): number {
  return min + rng() * (max - min)
}

function wrap360(deg: number): number {
  let d = deg % 360
  if (d < 0) d += 360
  return d
}
